import axiosInstance from './api';

const getApiErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (typeof error === 'string') return error;
  return error.message || error?.error?.message || error?.data?.message || fallback;
};

/**
 * GET /admin/partner/events
 * @param {{ tab?: string, page?: number, limit?: number, search?: string, types?: string }} params
 */
export const getPartnerEvents = async (params = {}) => {
  try {
    const query = {};

    if (params.tab) query.tab = params.tab;
    if (params.page != null) query.page = params.page;
    if (params.limit != null) query.limit = params.limit;
    if (params.search) query.search = params.search;
    if (params.types) query.types = params.types;

    const response = await axiosInstance.get('/admin/partner/events', { params: query });

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to load events'),
      error,
    };
  }
};

/**
 * GET /admin/partner/events/:id
 */
export const getPartnerEventById = async (eventId) => {
  try {
    const response = await axiosInstance.get(`/admin/partner/events/${eventId}`);

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to load event'),
      error,
    };
  }
};

/**
 * POST /admin/partner/add-event
 * multipart/form-data + saveAs=draft|submit
 */
export const addPartnerEvent = async (formData) => {
  try {
    const response = await axiosInstance.post('/admin/partner/add-event', formData);
    return {
      success: true,
      data: response.data,
      message: response.data?.message,
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to create event'),
      error,
    };
  }
};

/**
 * PUT /admin/partner/edit-event/:id
 * multipart/form-data + saveAs=draft|submit (editable when draft or rejected)
 */
export const editPartnerEvent = async (eventId, formData) => {
  try {
    const response = await axiosInstance.put(`/admin/partner/edit-event/${eventId}`, formData);
    return {
      success: true,
      data: response.data,
      message: response.data?.message,
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to update event'),
      error,
    };
  }
};

/**
 * POST /admin/partner/events/:id/submit
 * status=submit -> no extra fields
 * status=resubmit -> pass changed fields inside `changes`
 */
export const submitPartnerEvent = async (eventId, payload = { status: 'submit' }) => {
  try {
    const response = await axiosInstance.post(`/admin/partner/events/${eventId}/submit`, payload);
    return {
      success: true,
      data: response.data,
      message: response.data?.message,
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to submit event'),
      error,
    };
  }
};

/**
 * PATCH /admin/partner/events/:id/attendance
 * Body: { actualAttendance, walkIns, noShows, operationalNotes }
 */
export const finalizePartnerEventAttendance = async (eventId, payload = {}) => {
  try {
    const response = await axiosInstance.patch(
      `/admin/partner/events/${eventId}/attendance`,
      payload,
    );
    return {
      success: true,
      data: response.data,
      message: response.data?.message,
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to finalize attendance'),
      error,
    };
  }
};

/**
 * POST /admin/partner/events/:id/archive
 */
export const archivePartnerEvent = async (eventId) => {
  try {
    const response = await axiosInstance.post(`/admin/partner/events/${eventId}/archive`);
    return {
      success: true,
      data: response.data,
      message: response.data?.message,
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to archive event'),
      error,
    };
  }
};

/**
 * DELETE /admin/partner/events/:id
 */
export const deletePartnerEvent = async (eventId) => {
  try {
    const response = await axiosInstance.delete(`/admin/partner/events/${eventId}`);
    return {
      success: true,
      data: response.data,
      message: response.data?.message,
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to delete event'),
      error,
    };
  }
};
